import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const { lang, setLanguage } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate('/');
    } catch (err) {
      // 401 = identifiants invalides → toujours utiliser la traduction locale,
      // car le backend renvoie un detail string en anglais ("Invalid email or password").
      if (err.response?.status === 401) {
        setError(t('login_error', lang));
      } else {
        const detail = err.response?.data?.detail;
        if (typeof detail === 'string') setError(detail);
        else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
        else setError(t('login_error', lang));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-6" style={{ background: '#0F0F1A' }}>
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 sm:gap-3 mb-8 sm:mb-10">
          <img src="https://customer-assets.emergentagent.com/job_forfait-downgrade/artifacts/t6wkkpkw_Logo%20apollo%20sans%20baseline%20-%20f77f00.png" alt="Apollo" className="h-10 sm:h-12 object-contain" />
          <span className="text-xl sm:text-2xl" style={{ color: '#6B7280' }}>/</span>
          <span className="text-xl sm:text-2xl font-medium" style={{ color: '#6B7280' }}>ticketing</span>
        </div>

        {/* Language toggle */}
        <div className="flex justify-center mb-8">
          <div className="flex rounded-full overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
            <button onClick={() => setLanguage('fr')} className="px-4 py-1.5 text-sm font-semibold transition-all" style={{ background: lang === 'fr' ? '#F77F00' : 'transparent', color: lang === 'fr' ? '#fff' : '#6B7280' }}>FR</button>
            <button onClick={() => setLanguage('en')} className="px-4 py-1.5 text-sm font-semibold transition-all" style={{ background: lang === 'en' ? '#F77F00' : 'transparent', color: lang === 'en' ? '#fff' : '#6B7280' }}>EN</button>
          </div>
        </div>

        {/* Card */}
        <div className="apollo-card p-5 sm:p-8">
          <h2 data-testid="login-title" className="text-xl font-bold text-white mb-6 text-center">{t('login', lang)}</h2>
          
          {error && (
            <div data-testid="login-error" className="mb-4 p-3 rounded-lg text-sm text-center" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#9CA3AF' }}>{t('email', lang)}</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="apollo-input"
                placeholder="nom@apollo.fr"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#9CA3AF' }}>{t('password', lang)}</label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="apollo-input pr-10"
                  placeholder="••••••••"
                  required
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#6B7280' }}>
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" id="remember" checked={remember} onChange={e => setRemember(e.target.checked)} className="w-4 h-4 rounded" style={{ accentColor: '#F77F00' }} />
              <label htmlFor="remember" className="text-sm" style={{ color: '#9CA3AF' }}>{t('remember_me', lang)}</label>
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="apollo-btn-primary w-full py-3 text-base"
              style={{ opacity: loading ? 0.7 : 1 }}
            >
              {loading ? t('loading', lang) : t('login_btn', lang)}
            </button>
          </form>
        </div>

        {/* Demo accounts info — gated par REACT_APP_SHOW_DEMO_CREDENTIALS pour ne JAMAIS apparaître en prod.
            Sécurité : leak d'emails APOLLO + mots de passe demo = risque RGPD + invitation au hacking.
            En preview, ajouter REACT_APP_SHOW_DEMO_CREDENTIALS=true dans /app/frontend/.env si besoin de la voir. */}
        {process.env.REACT_APP_SHOW_DEMO_CREDENTIALS === 'true' && (
          <div data-testid="demo-accounts" className="mt-6 p-4 rounded-xl text-center" style={{ background: 'rgba(247,127,0,0.05)', border: '1px solid rgba(247,127,0,0.1)' }}>
            <p className="text-xs font-medium mb-2" style={{ color: '#F77F00' }}>{t('demo_accounts_title', lang)}</p>
            <div className="space-y-1 text-xs" style={{ color: '#9CA3AF' }}>
              <p>Member: marie@apollo.fr / member123</p>
              <p>Consultant: sophie@apollo.fr / consultant123</p>
              <p>DR: claire.dr@apollo.fr / dr123</p>
              <p>DN: marc.dn@apollo.fr / dn123</p>
              <p>Support: support@apollo.fr / support123</p>
              <p>Direction: admin@apollo.fr / direction123</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
